import styled, {css, keyframes} from "styled-components";
import {popRise, pulse} from "../../SharedComponents/Animations";

/**
 * Node card and node-related UI components for the assembly planner
 */
export const AssemblyNodes = {
    NodeCard: styled.div.attrs<{ $x: number; $y: number; $kind: string; $highlight: boolean; $active?: boolean }>(props => ({
      style: {
        left: `${props.$x}%`,
        top: `${props.$y}%`,
      }
    }))<{ $x: number; $y: number; $kind: string; $highlight: boolean; $active?: boolean }>`
      position: absolute;
      transform: translate(-50%, -50%);
      min-width: ${props => props.$kind === "assembler" ? "170px" : "150px"};
      max-width: 220px;
      border-radius: ${props => props.$kind === "source" ? "999px" : "14px"};
      padding: ${props => props.$kind === "source" ? "10px 14px" : "10px"};
      background: ${props => props.$highlight ? "#ecfdf3" : "#ffffff"};
      border: 2px solid ${props => props.$highlight ? "#22c55e" : "#d9e2ec"};
      box-shadow: 0 4px 10px rgba(15, 23, 42, 0.08);
      color: #0f172a;
      cursor: grab;
      user-select: none;
      pointer-events: auto;
      touch-action: none;
      &:active {
        cursor: grabbing;
      }
      ${props => props.$active ? css`
        animation: ${pulse} 1.5s ease-in-out infinite;
      ` : ""}
    `,
    InventoryPulseStack: styled.div`
      position: absolute;
      top: -12px;
      right: -12px;
      display: flex;
      flex-direction: column;
      gap: 4px;
      align-items: flex-end;
      pointer-events: none;
    `,
    InventoryPulse: styled.div`
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 4px 10px;
      background: rgba(13, 110, 253, 0.12);
      border-radius: 999px;
      color: #0d6efd;
      font-weight: 700;
      box-shadow: 0 4px 10px rgba(15, 23, 42, 0.15);
      animation: ${popRise} 820ms ease forwards;
      font-size: 13px;
    `,
    NodeTitle: styled.div`
      font-weight: 700;
      display: flex;
      align-items: center;
      gap: 8px;
      position: relative;
    `,
    DeleteButton: styled.button`
      position: absolute;
      top: -6px;
      right: -6px;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      border: 1px solid #dc2626;
      background: #fef2f2;
      color: #dc2626;
      font-size: 14px;
      font-weight: 700;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      line-height: 1;
      padding: 0;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      &:hover {
        background: #dc2626;
        color: white;
      }
    `,
    NodeMeta: styled.div`
      font-size: 12px;
      color: #475569;
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
      align-items: center;
    `,
    RecipeButton: styled.button`
      border: 1px solid #dbeafe;
      background: #eff6ff;
      padding: 6px;
      border-radius: 10px;
      display: inline-flex;
      align-items: center;
      cursor: pointer;
      box-shadow: inset 0 1px 0 rgba(255,255,255,0.65);
    `,
    RecipeSelectButton: styled.button`
      width: 100%;
      border: 2px solid #dbeafe;
      background: #eff6ff;
      padding: 10px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      gap: 10px;
      cursor: pointer;
      margin-bottom: 8px;
      transition: all 0.15s ease;
      &:hover {
        border-color: #3b82f6;
        background: #dbeafe;
        box-shadow: 0 2px 8px rgba(59, 130, 246, 0.2);
      }
      &:active {
        transform: scale(0.98);
      }
    `,
    RecipeChangeIcon: styled.div`
      font-size: 18px;
      color: #3b82f6;
      flex-shrink: 0;
    `,
    NodeColumn: styled.div`
      display: flex;
      flex-direction: column;
      gap: 2px;
      min-width: 0;
    `,
    NodeLabel: styled.div`
      font-size: 13px;
      font-weight: 700;
      color: #0f172a;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    `,
    SubLabel: styled.div`
      font-size: 11px;
      color: #475569;
    `,
    RecipePopover: styled.div`
      position: absolute;
      top: 100%;
      left: 0;
      margin-top: 6px;
      background: white;
      border: 1px solid #dbeafe;
      box-shadow: 0 10px 25px rgba(15, 23, 42, 0.2);
      border-radius: 10px;
      padding: 10px;
      z-index: 5;
      min-width: 220px;
    `,
    RecipeGrid: styled.div`
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(90px, 1fr));
      gap: 6px;
    `,
    RecipeOption: styled.button<{ $active?: boolean }>`
      border: 1px solid ${props => props.$active ? "#22c55e" : "#e2e8f0"};
      background: ${props => props.$active ? "#ecfdf3" : "#f8fafc"};
      color: #0f172a;
      padding: 6px;
      border-radius: 8px;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 4px;
      cursor: pointer;
    `,
    IngredientItem: styled.span`
      display: inline-flex;
      align-items: center;
      gap: 4px;
      position: relative;
    `,
    IngredientWarning: styled.span`
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: #ef4444;
      color: white;
      font-size: 10px;
      font-weight: 700;
      cursor: help;
      animation: ${keyframes`
        0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7); }
        50% { transform: scale(1.1); box-shadow: 0 0 0 4px rgba(239, 68, 68, 0); }
      `} 2s ease-in-out infinite;
    `,
    ConnectionHandle: styled.div<{ $position: 'left' | 'right', $isHighlighted?: boolean, $blink?: boolean }>`
      position: absolute;
      ${props => props.$position === 'left' ? 'left: -12px;' : 'right: -12px;'}
      top: 50%;
      transform: translateY(-50%);
      width: 28px;
      height: 28px;
      border-radius: 4px;
      background: ${props => props.$isHighlighted ? '#22c55e' : props.$position === 'left' ? '#f97316' : '#3b82f6'};
      border: 2px solid white;
      cursor: ${props => props.$position === 'right' ? 'grab' : 'default'};
      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      z-index: 10;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 16px;
      font-weight: 700;
      transition: all 0.15s ease;
      ${props => props.$blink ? css`
        animation: ${keyframes`
          0%, 100% { opacity: 1; box-shadow: 0 2px 4px rgba(0,0,0,0.3); }
          50% { opacity: 0.3; box-shadow: 0 0 12px ${props.$position === 'left' ? '#f97316' : '#3b82f6'}; }
        `} 1.5s ease-in-out infinite;
      ` : ''}
      &:hover {
        ${props => props.$position === 'right' ? `
          background: #2563eb;
          transform: translateY(-50%) scale(1.15);
          cursor: grab;
        ` : props.$isHighlighted ? `
          background: #16a34a;
          transform: translateY(-50%) scale(1.15);
        ` : ''}
      }
      &:active {
        ${props => props.$position === 'right' ? 'cursor: grabbing;' : ''}
      }
      &::before {
        content: '→';
        font-size: 14px;
      }
    `,
};
